/**
 * Alarm WebSocket client - connects to backend WebSocket endpoint
 * for real-time alarm notifications. JWT token is passed via query
 * parameter and verified by backend handshake interceptor.
 *
 * 协议自动推导：HTTPS 页面 → wss://（生产走 nginx 443 反代）；
 * HTTP 页面 → ws://（开发直连 backend 8080）。
 */

let ws = null
let reconnectTimer = null
let messageHandlers = []

const WS_BASE = process.env.VUE_APP_ALARM_WS_URL || buildWsBase()

/**
 * 根据当前页面协议与 host 推导 WebSocket 基础 URL
 * - HTTPS 页面：wss://<host>/api/ws/alarm（生产，走 nginx 443 反代到 backend 8080）
 * - HTTP 页面：ws://<hostname>:8080/api/ws/alarm（开发，直连 backend）
 */
function buildWsBase() {
  const { protocol, hostname, host } = window.location
  if (protocol === 'https:') {
    return `wss://${host}/api/ws/alarm`
  }
  return `ws://${hostname}:8080/api/ws/alarm`
}

/**
 * Connect to alarm WebSocket server
 */
export function connectAlarmWebSocket() {
  if (ws && ws.readyState === WebSocket.OPEN) {
    return
  }

  try {
    const token = localStorage.getItem('token') || ''
    const url = `${WS_BASE}?token=${encodeURIComponent(token)}`
    ws = new WebSocket(url)

    ws.onopen = () => {
      console.log('[AlarmWS] Connected to alarm WebSocket server')
      if (reconnectTimer) {
        clearTimeout(reconnectTimer)
        reconnectTimer = null
      }
    }

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        messageHandlers.forEach(handler => {
          try {
            handler(data)
          } catch (e) {
            console.error('[AlarmWS] Error in message handler:', e)
          }
        })
      } catch (e) {
        console.error('[AlarmWS] Error parsing message:', e)
      }
    }

    ws.onclose = () => {
      console.log('[AlarmWS] Disconnected from alarm WebSocket server')
      scheduleReconnect()
    }

    ws.onerror = (error) => {
      console.error('[AlarmWS] WebSocket error:', error)
      ws.close()
    }
  } catch (e) {
    console.error('[AlarmWS] Error connecting:', e)
    scheduleReconnect()
  }
}

/**
 * Schedule reconnection attempt
 */
function scheduleReconnect() {
  if (reconnectTimer) return
  reconnectTimer = setTimeout(() => {
    console.log('[AlarmWS] Attempting to reconnect...')
    connectAlarmWebSocket()
  }, 5000)
}

/**
 * Disconnect from alarm WebSocket server
 */
export function disconnectAlarmWebSocket() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
  if (ws) {
    ws.close()
    ws = null
  }
}

/**
 * Register a message handler
 * @param {Function} handler - function to call when alarm message is received
 */
export function onAlarmMessage(handler) {
  if (typeof handler === 'function') {
    messageHandlers.push(handler)
  }
}

/**
 * Remove a message handler
 * @param {Function} handler - the handler function to remove
 */
export function offAlarmMessage(handler) {
  messageHandlers = messageHandlers.filter(h => h !== handler)
}

export default {
  connect: connectAlarmWebSocket,
  disconnect: disconnectAlarmWebSocket,
  onMessage: onAlarmMessage,
  offMessage: offAlarmMessage
}
